/**
 * Service for dynamically updating meta tags for social media sharing
 */

class MetaTagsService {
  constructor() {
    this.centerName = null;
    // Try to load center name from cached config
    try {
      const cached = localStorage.getItem('centerConfig');
      if (cached) {
        const config = JSON.parse(cached);
        this.centerName = config?.name || null;
      }
    } catch { /* ignore */ }
  }

  /**
   * Update meta tags for a specific page
   * @param {Object} options - Meta tag options
   * @param {string} options.title - Page title
   * @param {string} options.description - Page description
   * @param {string} options.image - Image URL for social media preview
   * @param {string} options.url - Canonical URL
   * @param {string} options.type - Open Graph type (e.g., 'website', 'video.other')
   * @param {Object} options.video - Video-specific meta data
   */
  updateMetaTags({
    title,
    description,
    image,
    url,
    type = 'website',
    video = null
  }) {
    // Update document title
    if (title) {
      document.title = title;
    }

    // Update or create meta tags
    this.setMetaTag('description', description);
    
    // Open Graph tags
    this.setMetaTag('og:title', title, 'property');
    this.setMetaTag('og:description', description, 'property');
    this.setMetaTag('og:image', image, 'property');
    this.setMetaTag('og:url', url, 'property');
    this.setMetaTag('og:type', type, 'property');
    
    // Twitter Card tags
    this.setMetaTag('twitter:card', video ? 'player' : 'summary_large_image', 'name');
    this.setMetaTag('twitter:title', title, 'name');
    this.setMetaTag('twitter:description', description, 'name');
    this.setMetaTag('twitter:image', image, 'name');
    
    // Video-specific tags
    if (video) {
      this.setMetaTag('og:video', video.url, 'property');
      this.setMetaTag('og:video:url', video.url, 'property');
      this.setMetaTag('og:video:secure_url', video.url, 'property');
      this.setMetaTag('og:video:type', 'text/html', 'property');
      this.setMetaTag('og:video:width', '1280', 'property');
      this.setMetaTag('og:video:height', '720', 'property');
      
      if (video.embedUrl) {
        this.setMetaTag('twitter:player', video.embedUrl, 'name');
        this.setMetaTag('twitter:player:width', '1280', 'name');
        this.setMetaTag('twitter:player:height', '720', 'name');
      }
    }
  }

  /**
   * Update meta tags for a YouTube short
   * @param {Object} shortData - Short video data
   */
  updateForShort(shortData) {
    if (!shortData) return;

    // Extract YouTube video ID from URL
    const videoId = this.extractYouTubeVideoId(shortData.shortUrl);
    
    // Try maxresdefault first, then fall back to hqdefault for better quality
    const primaryThumbnail = videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
    const fallbackThumbnail = videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;
    const shareUrl = `${window.location.origin}/shorts?v=${encodeURIComponent(shortData.shortUrl)}`;

    // Use primary thumbnail initially
    let thumbnailUrl = primaryThumbnail;

    this.updateMetaTags({
      title: shortData.title || 'Short Video',
      description: `Watch "${shortData.title}" by ${shortData.speakerName || 'Speaker'} on ${this.centerName || 'our platform'}`,
      image: thumbnailUrl,
      url: shareUrl,
      type: 'video.other',
      video: {
        url: shortData.shortUrl,
        embedUrl: videoId ? `https://www.youtube.com/embed/${videoId}` : null
      }
    });

    // Check if maxresdefault exists, fallback to hqdefault if not
    if (primaryThumbnail) {
      const img = new Image();
      img.onerror = () => {
        // Update to fallback thumbnail if primary fails
        this.setMetaTag('og:image', fallbackThumbnail, 'property');
        this.setMetaTag('og:image:secure_url', fallbackThumbnail, 'property');
        this.setMetaTag('twitter:image', fallbackThumbnail, 'name');
      };
      img.src = primaryThumbnail;
    }

    // Add additional meta tags for better SMS preview support
    this.setMetaTag('og:image:width', '1280', 'property');
    this.setMetaTag('og:image:height', '720', 'property');
    this.setMetaTag('og:image:secure_url', thumbnailUrl, 'property');
    
    // Ensure description meta tag is also updated
    this.setMetaTag('description', `Watch "${shortData.title}" by ${shortData.speakerName || 'Speaker'} on ${this.centerName || 'our platform'}`);
  }

  /**
   * Reset meta tags to defaults
   */
  resetToDefaults() {
    // Try to get the config from localStorage if available
    let configData = null;
    try {
      const cachedConfig = localStorage.getItem('centerConfig');
      if (cachedConfig) {
        configData = JSON.parse(cachedConfig);
      }
    } catch (e) {
      console.error('Error reading cached config:', e);
    }

    const defaultTitle = configData?.name || 'Community Hub';
    const defaultDescription = configData?.description || `Access lecture summaries, videos, and notes from ${configData?.name || 'our center'}`;
    const defaultImage = configData?.logo_url || `${window.location.origin}/images/og-default.jpg`;
    const defaultUrl = window.location.origin;

    this.updateMetaTags({
      title: defaultTitle,
      description: defaultDescription,
      image: defaultImage,
      url: defaultUrl,
      type: 'website'
    });

    // Reset document title
    document.title = defaultTitle;
  }

  /**
   * Helper to set or update a meta tag
   */
  setMetaTag(key, value, attribute = 'name') {
    if (!value) return;

    let metaTag = document.querySelector(`meta[${attribute}="${key}"]`);
    
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.setAttribute(attribute, key);
      document.head.appendChild(metaTag);
    }
    
    metaTag.setAttribute('content', value);
    
    // Debug logging for development
    if (process.env.NODE_ENV === 'development') {
      console.log(`Meta tag updated: ${attribute}="${key}" content="${value}"`);
    }
  }

  /**
   * Extract YouTube video ID from URL
   */
  extractYouTubeVideoId(url) {
    if (!url) return null;
    
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/shorts\/([^&\n?#]+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    
    return null;
  }
}

// Export singleton instance
export const metaTagsService = new MetaTagsService();